import React, { useEffect, useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Typography } from '@material-ui/core'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'

import effects from 'app/effects/effects'
import { useAppName } from 'commons/usePartner'
import { SECONDARY } from 'app/constants/input'
import { messageAlertError } from 'commons/error'
import MessageAlert from 'app/components/MessageAlert'
import Switchs from 'app/componentV4/Switchs'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'

const PlacePublic = ({ checked = false }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const appName = useAppName()

  const [isDialogOpen, setDialogOpen] = useState(false)
  const [isPublic, setPublic] = useState(checked)

  const dialogConfig = useMemo(
    () => ({
      hidden: {
        title: 'profile.dialog.makePrivate.title',
        body: 'profileV4.dialog.makePrivate.description',
        subText: 'profileV4.dialog.makePrivate.description.subText',
        button: 'profile.dialog.makePrivate.button.label',
      },
      public: {
        title: 'profile.dialog.makePublic.title',
        body: 'profileV4.dialog.makePublic.description',
        subText: 'profileV4.dialog.makePublic.description.subText',
        button: 'profile.dialog.makePublic.button.label',
      },
    }),
    [],
  )

  const updatePlacePublic = useMutation({
    mutationFn: (status) =>
      effects.apis.profileInfo.placePublic({ mobileVisibilityStatus: status }),
    onSuccess: () => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'text.change.success' }),
        }),
      )
      setDialogOpen(false)
    },
    onError: (error) => {
      const msg = error?.response?.data || error.message
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(msg, formatMessage),
        }),
      )
      setPublic(checked)
      setDialogOpen(false)
    },
  })

  // Sync external checked prop
  useEffect(() => {
    setPublic(checked)
  }, [checked])

  const handleToggle = useCallback((event) => {
    const next = event.target.checked
    setPublic(next)
    setDialogOpen(true)
  }, [])

  const handleConfirm = useCallback(() => {
    updatePlacePublic.mutate(isPublic)
  }, [updatePlacePublic, isPublic])

  const currentConfig = isPublic ? dialogConfig.public : dialogConfig.hidden

  return (
    <div className="flex items-center">
      <Switchs
        id="switch-place-public"
        checked={isPublic}
        disabled={isDialogOpen || updatePlacePublic.isPending}
        onChange={handleToggle}
        role="switch"
        aria-checked={isPublic}
        inputProps={{ 'aria-label': 'Make place public toggle' }}
      />
      <Typography className="text-14 text-primary font-400 mx-3">
        {isPublic
          ? formatMessage({ id: 'profile.toggle.place.public' })
          : formatMessage({ id: 'profile.toggle.place.private' })}
      </Typography>

      <SubmitDialog
        id="dialog-confirm-place-public"
        open={isDialogOpen}
        type={SECONDARY}
        title={formatMessage({ id: currentConfig.title })}
        submitText={formatMessage({ id: currentConfig.button })}
        closeText={formatMessage({ id: 'action.cancel' })}
        onSubmit={handleConfirm}
        onClose={() => {
          setPublic((prev) => !prev)
          setDialogOpen(false)
        }}
        isLoading={updatePlacePublic.isPending}
        disabledSubmit={updatePlacePublic.isPending}
        textAlign="left"
      >
        <Typography className="text-md text-primary text-left mt-10">
          <FormattedMessage id={currentConfig.body} values={{ appName }} />
        </Typography>

        <Typography className="text-md text-primary text-left mt-10 mb-10">
          <FormattedMessage id={currentConfig.subText} values={{ appName }} />
        </Typography>
      </SubmitDialog>
    </div>
  )
}

PlacePublic.propTypes = {
  checked: PropTypes.bool,
}

export default PlacePublic
